/**
 * <AdminAudienceNotice> — the be-788 / CC-431 login error surface for
 * AUTH_ADMIN_AUDIENCE_NOT_PERMITTED.
 *
 * The account's credentials were RIGHT, but it holds no admin-capable role, so
 * this sign-in can never succeed here. The backend sends a pre-localized RFC
 * 7807 `title` ("Use the employee app" / "استخدم تطبيق الموظفين") — that title
 * IS the copy (C5); we never render the generic bad-credentials message for
 * this code. The notice links to the employee app on the same origin, reusing
 * the core-owned `auth:session.open_employee_app` label (same pattern as
 * <ForceLogoutAlert reason="admin_access_revoked">).
 *
 * Shared by LoginForm / OtpForm / TotpForm — the 403 can land on any of the
 * three verify calls.
 */
import type { JSX } from "react";
import { useTranslation } from "react-i18next";
import { Alert } from "@linkhr/design-system";
import { EMPLOYEE_APP_PATH } from "../constants/appPaths.js";

/** The wire code this notice exists for (kept here so the three forms share one literal). */
export const ADMIN_AUDIENCE_ERROR_CODE = "AUTH_ADMIN_AUDIENCE_NOT_PERMITTED";

export interface AdminAudienceNoticeProps {
  /** The server's pre-localized title (ApiError.title === err.message). */
  serverTitle: string;
  /** Where the link goes. @default the employee app */
  href?: string;
  /** The link's text. @default "Open the employee app" */
  linkLabel?: string;
}

export function AdminAudienceNotice({
  serverTitle,
  href = EMPLOYEE_APP_PATH,
  linkLabel,
}: AdminAudienceNoticeProps): JSX.Element {
  const { t } = useTranslation("auth");
  return (
    <Alert
      variant="danger"
      description={
        <>
          {serverTitle}{" "}
          <a href={href} className="text-primary underline">
            {linkLabel ?? t("session.open_employee_app")}
          </a>
        </>
      }
    />
  );
}
